export const TOP_LIST_CAROUSEL_CARDS_PER_PAGE_WIDE = 2;
export const TOP_LIST_CAROUSEL_CARDS_PER_PAGE_NARROW = 1;

export function topListCarouselCardsPerPage(isWideLayout: boolean): number {
  return isWideLayout
    ? TOP_LIST_CAROUSEL_CARDS_PER_PAGE_WIDE
    : TOP_LIST_CAROUSEL_CARDS_PER_PAGE_NARROW;
}

export function topListCarouselMaxPage(itemCount: number, cardsPerPage: number): number {
  if (itemCount <= 0 || cardsPerPage <= 0) {
    return 0;
  }

  return Math.max(0, Math.ceil(itemCount / cardsPerPage) - 1);
}

export function topListCarouselVisibleItems<T>(
  items: T[],
  selectedPage: number,
  cardsPerPage: number
): T[] {
  const startIndex = selectedPage * cardsPerPage;
  return items.slice(startIndex, startIndex + cardsPerPage);
}

/** Fixed-width TopList tile used by classic dashboard grid. */
export const TOP_LIST_ITEM_WIDTH_PX = 260;

/** theme spacing(2) gap between two cards. */
export const TOP_LIST_GRID_GAP_PX = 16;

/**
 * Drop to carousel when the container can no longer fit two 260px tiles + gap.
 * Matches CSS `repeat(auto-fill, minmax(260px, 1fr))` wrap point.
 */
export const TOP_LIST_TWO_COLUMN_MIN_WIDTH_PX = TOP_LIST_ITEM_WIDTH_PX * 2 + TOP_LIST_GRID_GAP_PX;

/**
 * Re-enter two-column grid only above this width.
 * Hysteresis (~40px) avoids flipping when the drawer opens/closes around ~520–550px.
 */
export const TOP_LIST_TWO_COLUMN_ENTER_WIDTH_PX = TOP_LIST_TWO_COLUMN_MIN_WIDTH_PX + 40;

type ShouldUseCarouselArgs = {
  containerWidthPx: number;
  locationCount: number;
  /** Previous carousel decision — enables hysteresis for the 2-location case. */
  wasUsingCarousel?: boolean;
};

/**
 * Classic dashboard TopList: use a carousel when the multi-card row would get too long.
 * Uses **measured container width** (drawer-aware), not viewport breakpoints.
 * - 0–1 locations: never carousel
 * - 2 locations: carousel with enter/exit hysteresis (see ENTER / MIN widths)
 * - 3+ locations: always carousel
 */
export function shouldUseDashboardTopListCarousel({
  containerWidthPx,
  locationCount,
  wasUsingCarousel = true
}: ShouldUseCarouselArgs): boolean {
  if (locationCount <= 1) {
    return false;
  }

  if (locationCount > 2) {
    return true;
  }

  // Exactly two: before first measure, prefer carousel to avoid a grid flash.
  if (containerWidthPx <= 0) {
    return true;
  }

  if (wasUsingCarousel) {
    // Stay on peek until clearly wide enough for two columns.
    return containerWidthPx < TOP_LIST_TWO_COLUMN_ENTER_WIDTH_PX;
  }

  // Already on grid: only switch back when tiles would wrap.
  return containerWidthPx < TOP_LIST_TWO_COLUMN_MIN_WIDTH_PX;
}
